import { useCallback, useState } from 'react';
import { CampaignType_Enum as CampaignType, Recipient } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { List, ListListsResponse } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2/service.pb';
import { UseInfiniteQueryResult } from 'react-query';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { BulkEmailPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import { Button, SearchField, useModalControl } from '@frontend/design-system';
import { DeleteListModal } from '../saved-list/delete-list-modal';
import { Mode } from '../types';
import { NewSavedListItem } from './new-saved-list-item';
import { SavedListModalContent } from './saved-list-modal-content';
import { useDebouncedSearch } from './use-debounced-search';

type Props = {
  campaignType: CampaignType;
  contactLists: List[];
  contactListsQueryResult: UseInfiniteQueryResult<ListListsResponse>;
  createContactList: (list: List) => Promise<void>;
  deleteContactList: (listId: string) => Promise<void>;
  deselectContactList: (listId: string) => void;
  recipientsToUpload: Recipient[];
  renameContactList: (list: List) => Promise<void>;
  searchQuery: string;
  selectContactList: (listId: string) => void;
  selectedContactLists: string[];
  setMode: (mode: Mode) => void;
  setRecipientsToUpload: (recipients: Recipient[]) => void;
  setSearchQuery: (searchQuery: string) => void;
};

export const SavedListModal = ({
  campaignType,
  contactLists,
  contactListsQueryResult,
  createContactList,
  deleteContactList,
  deselectContactList,
  recipientsToUpload,
  renameContactList,
  searchQuery,
  selectContactList,
  selectedContactLists,
  setMode,
  setRecipientsToUpload,
  setSearchQuery,
}: Props) => {
  const { t } = useTranslation('bulk-messaging');

  const { searchFieldProps, isDebouncing } = useDebouncedSearch(
    searchQuery,
    setSearchQuery,
    contactListsQueryResult.isFetched
  );
  const [listIdToDelete, setListIdToDelete] = useState<string | undefined>(undefined);
  const [listIdToRename, setListIdToRename] = useState<string | undefined>(undefined);
  const { modalProps: deleteModalProps, triggerProps: deleteTriggerProps } = useModalControl();

  const handleCheck = useCallback(
    (listId?: string) => {
      if (!listId) return;
      selectedContactLists.includes(listId) ? deselectContactList(listId) : selectContactList(listId);
    },
    [deselectContactList, selectContactList, selectedContactLists]
  );

  const handleDelete = useCallback(
    (listId?: string) => {
      deleteTriggerProps.onClick();
      setListIdToDelete(listId);
    },
    [deleteTriggerProps]
  );

  return (
    <>
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          alignItems: 'center',
          columnGap: theme.spacing(1),
          margin: theme.spacing(2, 0),
        }}
      >
        <SearchField
          {...searchFieldProps}
          name='search-lists'
          placeholder={t('Search Lists')}
          aria-label={t('Search Lists')}
          maxLength={70}
          data-trackingid={`${BulkEmailPrefixes.Audience}-contacts-list-search-input`}
        />
        <Button
          variant='tertiary'
          hoverLabel={t('Upload CSV List')}
          aria-label={t('Upload CSV List')}
          onClick={() => setMode('csv')}
          trackingId={`${BulkEmailPrefixes.Audience}-contacts-upload-csv-btn`}
          css={{ padding: theme.spacing(1) }}
        >
          <Icon name='upload' color='default' />
        </Button>
      </div>

      {recipientsToUpload.length > 0 && (
        <NewSavedListItem
          campaignType={campaignType}
          createContactList={createContactList}
          recipientsToUpload={recipientsToUpload}
          setRecipientsToUpload={setRecipientsToUpload}
        />
      )}

      <SavedListModalContent
        isDebouncing={isDebouncing}
        contactLists={contactLists}
        listIdToRename={listIdToRename}
        renameContactList={renameContactList}
        contactListsQueryResult={contactListsQueryResult}
        handleCheck={handleCheck}
        handleDelete={handleDelete}
        selectedContactLists={selectedContactLists}
        setListIdToRename={setListIdToRename}
        recipientsToUpload={recipientsToUpload}
        setMode={setMode}
      />

      <DeleteListModal
        deleteContactList={deleteContactList}
        listIdToDelete={listIdToDelete}
        modalProps={deleteModalProps}
        setListIdToDelete={setListIdToDelete}
      />
    </>
  );
};
